import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../settings";

/**
 * Controls tension spine animations for individual Hold and Win symbol positions
 * Manages ONLY tension animations - completely independent from multiplier animations
 */
class HoldAndWinTensionOverlayController {
  constructor(container, reelIndex, symbolIndex, symbolWidth, symbolHeight) {
    this.container = container;
    this.reelIndex = reelIndex;
    this.symbolIndex = symbolIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this.tensionSpineData = {
      skeleton: "hns_multiplier-skeleton",
      atlas: "hns_multiplier-atlas",
    };

    this.tensionSpine = null;
    this.isVisible = false;
    this.initialized = false;

    // Track active timeouts for cleanup
    this.currentTimeouts = [];

    // Internal caches for performance optimization
    this._availableAnimationsCache = null; // Cache for getAvailableAnimations result
    this._spineScaleCache = new Map(); // Cache for calculateSpineScale results
    this._tensionOutDurationCache = null; // Cache for tension_out duration
    this._tensionInDurationCache = null; // Cache for tension_in duration
    this._tensionIdleDurationCache = null; // Cache for tension_idle duration
    this._lastSymbolWidth = null; // Track symbol width changes for cache invalidation
    this._lastSymbolHeight = null; // Track symbol height changes for cache invalidation

    this.initializeTensionSpine();
  }

  /**
   * Calculate responsive scale and positioning for spine animation
   * Matches multiplier spine scaling implementation
   */
  calculateSpineScale(
    spineInstance,
    containerWidth,
    containerHeight,
    skipAnimationSetup = false
  ) {
    // Check cache (only if dimensions match)
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (this._spineScaleCache.has(cacheKey)) {
      const cached = this._spineScaleCache.get(cacheKey);
      // Return cached result but still handle animation setup if needed
      if (
        !skipAnimationSetup &&
        spineInstance &&
        spineInstance.state &&
        spineInstance.state.data &&
        spineInstance.state.data.skeletonData.animations.length > 0
      ) {
        const defaultAnimation =
          spineInstance.state.data.skeletonData.animations[0];
        spineInstance.state.addAnimation(0, defaultAnimation.name, true, 0);
      }
      return cached;
    }

    if (
      !skipAnimationSetup &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.addAnimation(0, defaultAnimation.name, true, 0);
    }

    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;

    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);

    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._spineScaleCache.set(cacheKey, result);
    
    // Limit cache size
    if (this._spineScaleCache.size > 20) {
      const firstKey = this._spineScaleCache.keys().next().value;
      this._spineScaleCache.delete(firstKey);
    }

    return result;
  }

  /**
   * Hide tension animation immediately
   */
  hideTension() {
    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      this.isVisible = false;
      return;
    }

    requestAnimationFrame(() => {
      if (!this.tensionSpine || this.tensionSpine.destroyed) {
        this.isVisible = false;
        return;
      }

      this.tensionSpine.alpha = 0.001;
      this.isVisible = false;

      if (this.tensionSpine.state) {
        try {
          this.tensionSpine.state.clearTracks();
        } catch (error) {
          // Silent fail
        }
      }
    });
  }

  /**
   * Force stop tension animations with smooth exit transition
   */
  forceStopTension() {
    this.clearAllTimeouts();

    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state ||
      !this.getAvailableAnimations().includes("tension_out")
    ) {
      this.hideTension();
      return;
    }

    const currentAnimation = this.tensionSpine.state.getCurrent(0);
    const isPlayingTension =
      currentAnimation &&
      (currentAnimation.animation.name === "tension_in" ||
        currentAnimation.animation.name === "tension_idle");

    if (isPlayingTension) {
      this.tensionSpine.state.setAnimation(0, "tension_out", false);

      // Use helper method to get precise animation duration
      const animationDuration = this.getTensionOutDuration();

      const timeoutId = setTimeout(() => {
        this.hideTension();
      }, animationDuration);

      this.currentTimeouts.push(timeoutId);
    } else {
      this.hideTension();
    }
  }

  /**
   * Clear all active timeouts
   */
  clearAllTimeouts() {
    this.currentTimeouts.forEach(clearTimeout);
    this.currentTimeouts = [];
  }

  /**
   * Play tension intro animation and transition to idle loop
   */
  playTensionIn() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return;
    }

    requestAnimationFrame(() => {
      if (
        !this.tensionSpine ||
        this.tensionSpine.destroyed ||
        !this.tensionSpine.state
      ) {
        return;
      }

      try {
        this.tensionSpine.state.clearTracks();
        this.tensionSpine.state.clearListeners();
        this.tensionSpine.state.apply(this.tensionSpine.skeleton);
        this.tensionSpine.skeleton.setToSetupPose();

        this.tensionSpine.alpha = 1;
        this.isVisible = true;

        const animations = this.getAvailableAnimations();

        if (
          animations.includes("tension_in") &&
          animations.includes("tension_idle")
        ) {
          this.tensionSpine.state.setAnimation(0, "tension_in", false);
          this.tensionSpine.state.addAnimation(0, "tension_idle", true, 0);
        } else if (animations.includes("tension_idle")) {
          this.tensionSpine.state.setAnimation(0, "tension_idle", true);
        } else if (animations.length > 0) {
          this.tensionSpine.state.setAnimation(0, animations[0], true);
        }
      } catch (error) {
        this.hideTension();
      }
    });
  }

  /**
   * Play tension exit animation
   */
  playTensionOut() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return;
    }

    this.clearAllTimeouts();

    requestAnimationFrame(() => {
      if (
        !this.tensionSpine ||
        this.tensionSpine.destroyed ||
        !this.tensionSpine.state
      ) {
        return;
      }

      try {
        this.tensionSpine.state.clearTracks();
        this.tensionSpine.state.clearListeners();
        this.tensionSpine.state.apply(this.tensionSpine.skeleton);
        this.tensionSpine.skeleton.setToSetupPose();

        const animations = this.getAvailableAnimations();

        let animationToPlay = null;

        if (animations.includes("tension_out")) {
          animationToPlay = "tension_out";
        } else if (animations.length > 0) {
          animationToPlay = animations[0];
        }

        if (animationToPlay) {
          this.tensionSpine.state.setAnimation(0, animationToPlay, false);

          // Use helper method to get precise animation duration
          const animationDuration = this.getTensionOutDuration();

          const timeoutId = setTimeout(() => {
            this.hideTension();
          }, animationDuration);
          this.currentTimeouts.push(timeoutId);
        } else {
          this.hideTension();
        }
      } catch (error) {
        this.hideTension();
      }
    });
  }

  /**
   * Update tension spine dimensions for responsive scaling
   */
  resizeTension(newSymbolWidth, newSymbolHeight) {
    if (
      newSymbolWidth === this.symbolWidth &&
      newSymbolHeight === this.symbolHeight
    ) {
      return;
    }

    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;

    // Invalidate caches when dimensions change
    this._spineScaleCache.clear();
    this._lastSymbolWidth = null;
    this._lastSymbolHeight = null;

    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.tensionSpine || this.tensionSpine.destroyed) {
        return;
      }

      try {
        this.applyTensionScaling();
      } catch (error) {
        // Silent fail
      }
    });
  }

  /**
   * Apply scaling and positioning to tension spine
   * Matches multiplier spine scaling (no height multiplier or y offset)
   */
  applyTensionScaling() {
    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      return;
    }

    const scaleData = this.calculateSpineScale(
      this.tensionSpine,
      this.symbolWidth,
      this.symbolHeight
    );

    if (this.tensionSpine.scale) {
      this.tensionSpine.scale.set(scaleData.scale);
    }

    if (this.tensionSpine.position) {
      this.tensionSpine.position.set(scaleData.x, scaleData.y);
    }
  }

  /**
   * Create tension spine instance
   */
  createTensionSpine() {
    try {
      if (!this.tensionSpineData) {
        return null;
      }

      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.tensionSpineData.skeleton) ||
        !assets.has(this.tensionSpineData.atlas)
      ) {
        return null;
      }

      const skeletonData = PIXI.Assets.get(this.tensionSpineData.skeleton);
      const atlasData = PIXI.Assets.get(this.tensionSpineData.atlas);

      if (!skeletonData || !atlasData) {
        return null;
      }

      const tensionSpine = Spine.from({
        skeleton: this.tensionSpineData.skeleton,
        atlas: this.tensionSpineData.atlas,
      });

      if (!tensionSpine || !tensionSpine.skeleton || !tensionSpine.state) {
        return null;
      }

      if (
        !tensionSpine.skeleton.data ||
        !tensionSpine.skeleton.data.animations
      ) {
        return null;
      }

      return tensionSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animation names
   */
  getAvailableAnimations() {
    // Return cached result if available
    if (this._availableAnimationsCache) {
      return this._availableAnimationsCache;
    }

    if (!this.tensionSpine?.skeleton?.data?.animations) {
      return [];
    }
    
    const animations = this.tensionSpine.skeleton.data.animations.map((anim) => anim.name);
    
    // Cache result
    this._availableAnimationsCache = animations;
    
    return animations;
  }

  /**
   * Get tension_out animation duration in milliseconds
   * @returns {number} Duration in milliseconds
   */
  getTensionOutDuration() {
    // Return cached result if available
    if (this._tensionOutDurationCache !== null) {
      return this._tensionOutDurationCache;
    }

    if (!this.tensionSpine?.skeleton?.data?.animations) {
      this._tensionOutDurationCache = 1000; // Fallback to 1 second
      return this._tensionOutDurationCache;
    }

    const tensionOutAnim = this.tensionSpine.skeleton.data.animations.find(
      (anim) => anim.name === "tension_out"
    );

    this._tensionOutDurationCache = tensionOutAnim ? tensionOutAnim.duration * 1000 : 1000;
    return this._tensionOutDurationCache;
  }

  /**
   * Get tension_in animation duration in milliseconds
   * @returns {number} Duration in milliseconds
   */
  getTensionInDuration() {
    // Return cached result if available
    if (this._tensionInDurationCache !== null) {
      return this._tensionInDurationCache;
    }

    if (!this.tensionSpine?.skeleton?.data?.animations) {
      this._tensionInDurationCache = 667; // Fallback from featuresConfig comment
      return this._tensionInDurationCache;
    }

    const tensionInAnim = this.tensionSpine.skeleton.data.animations.find(
      (anim) => anim.name === "tension_in"
    );

    this._tensionInDurationCache = tensionInAnim ? tensionInAnim.duration * 1000 : 667;
    return this._tensionInDurationCache;
  }

  /**
   * Get tension_idle animation duration in milliseconds
   * @returns {number} Duration in milliseconds
   */
  getTensionIdleDuration() {
    // Return cached result if available
    if (this._tensionIdleDurationCache !== null) {
      return this._tensionIdleDurationCache;
    }

    if (!this.tensionSpine?.skeleton?.data?.animations) {
      this._tensionIdleDurationCache = 1000; // Fallback from featuresConfig comment
      return this._tensionIdleDurationCache;
    }

    const tensionIdleAnim = this.tensionSpine.skeleton.data.animations.find(
      (anim) => anim.name === "tension_idle"
    );

    this._tensionIdleDurationCache = tensionIdleAnim ? tensionIdleAnim.duration * 1000 : 1000;
    return this._tensionIdleDurationCache;
  }

  /**
   * Warm up tension spine with "tension_in" animation
   */
  warmUpTensionSpine() {
    if (!this.tensionSpine || !this.tensionSpine.state) {
      return;
    }

    try {
      const animations = this.getAvailableAnimations();
      const inAnimation = animations.find((anim) => anim === "tension_in");

      if (inAnimation) {
        this.tensionSpine.state.setAnimation(0, "tension_in", false);
        this.tensionSpine.state.update(0.016);
        this.tensionSpine.skeleton.updateWorldTransform();
        this.tensionSpine.state.clearTracks();
      }

      this.tensionSpine.alpha = 0.001;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Initialize tension spine and add to container
   * Matches multiplier spine initialization pattern
   */
  initializeTensionSpine() {
    this.tensionSpine = this.createTensionSpine();

    if (!this.tensionSpine) {
      return;
    }

    const missingComponents = [];
    if (!this.tensionSpine.skeleton) missingComponents.push("skeleton");
    if (!this.tensionSpine.state) missingComponents.push("state");
    if (!this.tensionSpine.skeleton?.data)
      missingComponents.push("skeleton.data");
    if (!this.tensionSpine.skeleton?.slots)
      missingComponents.push("skeleton.slots");
    if (!this.tensionSpine.skeleton?.bones)
      missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    const scaleData = this.calculateSpineScale(
      this.tensionSpine,
      this.symbolWidth,
      this.symbolHeight,
      true // Skip animation setup during initialization
    );

    this.tensionSpine.position.set(scaleData.x, scaleData.y);
    this.tensionSpine.scale.set(scaleData.scale);
    this.tensionSpine.zIndex = 5;
    this.tensionSpine.alpha = 0.001;
    this.isVisible = false;

    this.container.addChild(this.tensionSpine);

    if (this.tensionSpine.parent !== this.container) {
      return;
    }

    this.warmUpTensionSpine();

    // Invalidate animation caches when spine is initialized (spine structure may have changed)
    this._availableAnimationsCache = null;
    this._tensionOutDurationCache = null;
    this._tensionInDurationCache = null;
    this._tensionIdleDurationCache = null;

    this.initialized = true;
  }

  /**
   * Clean up spine resources and references
   */
  cleanup() {
    this.clearAllTimeouts();

    if (this.tensionSpine && !this.tensionSpine.destroyed) {
      try {
        if (this.tensionSpine.state) {
          this.tensionSpine.state.clearTracks();
          this.tensionSpine.state.clearListeners();
        }

        if (this.tensionSpine.parent) {
          this.tensionSpine.parent.removeChild(this.tensionSpine);
        }

        this.tensionSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    // Clear caches
    this._availableAnimationsCache = null;
    this._spineScaleCache.clear();
    this._tensionOutDurationCache = null;
    this._tensionInDurationCache = null;
    this._tensionIdleDurationCache = null;
    this._lastSymbolWidth = null;
    this._lastSymbolHeight = null;

    this.tensionSpine = null;
    this.container = null;
    this.tensionSpineData = null;
    this.isVisible = false;
    this.initialized = false;
  }
}

export default HoldAndWinTensionOverlayController;
